import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiAddLine, RiMegaphoneLine } from '@remixicon/react'
import {
  ANNOUNCEMENT_LEVELS,
  ANNOUNCEMENT_LIMITS,
  type AdminAnnouncement,
  type AnnouncementLevel,
} from 'shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  Input,
  PageHeader,
  SectionSpinner,
  Select,
  Switch,
} from '../../components/ui'
import { LEVEL_CLASSES } from '../../components/announcements/announcementStyles'
import { notifyAnnouncementsChanged } from '../../hooks/useAnnouncements'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'
import { formatDateTime, parseApiDate } from '../../lib/dates'
import { cx } from '../../lib/utils'

const BASE = '/api/admin/announcements'

// The server's rule (today to a year away, UTC), so the picker says it before the form is sent.
const EARLIEST = new Date().toISOString().slice(0, 10)
const LATEST = new Date(Date.now() + 365 * 86_400_000).toISOString().slice(0, 10)

type Status = 'active' | 'expired' | 'withdrawn'

function statusOf(announcement: AdminAnnouncement): Status {
  if (announcement.revoked_at) return 'withdrawn'
  return parseApiDate(announcement.expires_at).getTime() > Date.now() ? 'active' : 'expired'
}

const STATUS_VARIANT = { active: 'success', expired: 'neutral', withdrawn: 'neutral' } as const

function NewAnnouncementDialog({
  open,
  onOpenChange,
  onSaved,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSaved: () => void
}) {
  const { t } = useTranslation()
  const [message, setMessage] = useState('')
  const [level, setLevel] = useState<AnnouncementLevel>('info')
  const [expiresAt, setExpiresAt] = useState('')
  const [dismissible, setDismissible] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // The form starts empty each time the dialog opens.
  useEffect(() => {
    if (!open) return
    setMessage('')
    setLevel('info')
    setExpiresAt('')
    setDismissible(true)
    setError(null)
  }, [open])

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSaving(true)
    setError(null)
    try {
      await api.post(BASE, { message, level, expires_at: expiresAt, dismissible })
      notifyAnnouncementsChanged()
      onSaved()
      onOpenChange(false)
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('announcements.newTitle')}</DialogTitle>
          <DialogDescription>{t('announcements.dialogHint')}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="mt-4 space-y-4">
          <Field
            label={t('announcements.message')}
            hint={`${message.length}/${ANNOUNCEMENT_LIMITS.maxMessageLength}`}
            required
          >
            <Input
              value={message}
              maxLength={ANNOUNCEMENT_LIMITS.maxMessageLength}
              onChange={(event) => setMessage(event.target.value)}
            />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('announcements.level')} required>
              <Select
                value={level}
                onChange={(event) => setLevel(event.target.value as AnnouncementLevel)}
                options={ANNOUNCEMENT_LEVELS.map((value) => ({
                  value,
                  label: t(`announcements.level_${value}`),
                }))}
              />
            </Field>
            <Field label={t('announcements.expiresAt')} hint={t('announcements.expiresHint')} required>
              <Input
                type="date"
                value={expiresAt}
                min={EARLIEST}
                max={LATEST}
                onChange={(event) => setExpiresAt(event.target.value)}
              />
            </Field>
          </div>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-gray-200 px-3 py-2.5 dark:border-gray-800">
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                {t('announcements.dismissible')}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('announcements.dismissibleHint')}</p>
            </div>
            <Switch
              checked={dismissible}
              onCheckedChange={setDismissible}
              aria-label={t('announcements.dismissible')}
            />
          </div>

          <div>
            <p className="mb-1.5 text-sm font-medium text-gray-900 dark:text-gray-50">
              {t('announcements.preview')}
            </p>
            <div className={cx('rounded-lg px-4 py-2.5 text-sm font-medium', LEVEL_CLASSES[level])}>
              <p className="break-words">{message.trim() || t('announcements.previewPlaceholder')}</p>
            </div>
          </div>

          {error && <Alert>{error}</Alert>}
          <DialogFooter>
            <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={saving} disabled={!message.trim() || !expiresAt}>
              {t('announcements.publish')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/**
 * The announcement bar everyone sees at the top of the app. An announcement is
 * not edited: to change one, withdraw it and write a new one. A withdrawn or
 * expired one stays in the list, with who wrote it.
 */
export default function Announcements() {
  const { t, i18n } = useTranslation()
  const [items, setItems] = useState<AdminAnnouncement[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)

  const load = useCallback(() => {
    return api
      .get<{ data: AdminAnnouncement[] }>(BASE)
      .then((response) => setItems(response.data))
      .catch((err) => setError(toErrorMessage(err) ?? t('errors.generic')))
      .finally(() => setLoading(false))
  }, [t])

  useEffect(() => {
    load()
  }, [load])

  const withdraw = async (id: string) => {
    setError(null)
    try {
      await api.delete(`${BASE}/${id}`)
      notifyAnnouncementsChanged()
      await load()
    } catch (err) {
      setError(toErrorMessage(err) ?? t('errors.generic'))
    }
  }

  const add = (
    <Button onClick={() => setDialogOpen(true)}>
      <RiAddLine className="-ml-1 mr-1.5 size-4" aria-hidden="true" />
      {t('announcements.new')}
    </Button>
  )

  return (
    <>
      <PageHeader title={t('announcements.title')} subtitle={t('announcements.subtitle')} actions={add} />

      {error && <Alert className="mb-4">{error}</Alert>}

      {loading ? (
        <SectionSpinner />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<RiMegaphoneLine className="size-6" aria-hidden="true" />}
          title={t('announcements.emptyTitle')}
          description={t('announcements.emptyDescription')}
        />
      ) : (
        <ul className="space-y-4">
          {items.map((announcement) => {
            const status = statusOf(announcement)
            return (
              <li key={announcement.id}>
                <Card className="p-5">
                  <div className={cx('rounded-lg px-4 py-2.5 text-sm font-medium', LEVEL_CLASSES[announcement.level])}>
                    <p className="break-words">{announcement.message}</p>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                      <Badge variant={STATUS_VARIANT[status]}>{t(`announcements.status_${status}`)}</Badge>
                      <span>
                        {t('announcements.until', { date: formatDateTime(announcement.expires_at, i18n.language) })}
                      </span>
                      {announcement.created_by_name && (
                        <span>· {t('announcements.by', { name: announcement.created_by_name })}</span>
                      )}
                      {!announcement.dismissible && <span>· {t('announcements.notDismissible')}</span>}
                    </div>
                    {status === 'active' && (
                      <ConfirmDialog
                        trigger={
                          <Button variant="secondary" size="sm">
                            {t('announcements.withdraw')}
                          </Button>
                        }
                        title={t('announcements.withdrawTitle')}
                        description={t('announcements.withdrawDescription')}
                        confirmLabel={t('announcements.withdraw')}
                        onConfirm={() => withdraw(announcement.id)}
                      />
                    )}
                  </div>
                </Card>
              </li>
            )
          })}
        </ul>
      )}

      <NewAnnouncementDialog open={dialogOpen} onOpenChange={setDialogOpen} onSaved={load} />
    </>
  )
}
